import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectorRef } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { PlusComponent } from '../../../../icons/plus';
import { CountryPickerComponent } from '../../../../shared/country-picker';
import { YearPickerComponent } from '../../../../shared/year-picker';
// Define interfaces for our form data
interface AverageDataBuildingRow {
  assetLabel: FormControl<string | null>;
  buildingType: FormControl<string | null>;
  epcRating: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  floorArea: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'building'>;
}

interface AverageDataVehicleRow {
  assetLabel: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  country: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  quantity: FormControl<string | null>;
  annualDistance: FormControl<string | null>;
  method: FormControl<'average'>;
  assetType: FormControl<'vehicle'>;
}

interface SubMeteredEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  amount: FormControl<string | null>;
  isSubMetered: FormControl<true>;
}

interface NonSubMeteredEntry {
  category: FormControl<string | null>;
  type: FormControl<string | null>;
  unit: FormControl<string | null>;
  totalBuildingEnergy: FormControl<string | null>;
  totalBuildingArea: FormControl<string | null>;
  leasedArea: FormControl<string | null>;
  occupancyRate: FormControl<number>;
  isSubMetered: FormControl<false>;
}

type EnergyEntry = FormGroup<SubMeteredEntry> | FormGroup<NonSubMeteredEntry>;

interface AssetSpecificBuildingRow {
  assetLabel: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  subMetered: FormControl<boolean>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'building'>;
}

interface AssetSpecificVehicleRow {
  assetLabel: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  assetCategory: FormControl<string | null>;
  subtype: FormControl<string | null>;
  quantity: FormControl<string | null>;
  annualDistance: FormControl<string | null>;
  method: FormControl<'asset-specific'>;
  assetType: FormControl<'vehicle'>;
}

interface LessorSpecificRow {
  lessorName: FormControl<string | null>;
  assetType: FormControl<string | null>;
  reportingYear: FormControl<string | null>;
  country: FormControl<string | null>;
  scope1Emissions: FormControl<string | null>;
  scope2Emissions: FormControl<string | null>;
  leasedQuantity: FormControl<string | null>;
  totalQuantity: FormControl<string | null>;
  occupancyRate: FormControl<number>;
  method: FormControl<'lessor-specific'>;
}

type FormRow =
  | { row: AverageDataBuildingRow }
  | { row: AverageDataVehicleRow }
  | { row: AssetSpecificBuildingRow; energyEntries?: EnergyEntry[] }
  | { row: AssetSpecificVehicleRow }
  | { row: LessorSpecificRow };

@Component({
  selector: 'app-category4-question4',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent,
    CountryPickerComponent,
    YearPickerComponent
  ],
  template: `
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <div class="container mx-auto">
      <div class="flex flex-wrap gap-4 sm:gap-6 mt-4 mb-4 ml-2">
        <div
          (click)="toggleUsage(true)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_yes }}</span>
        </div>
        <div
          (click)="toggleUsage(false)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="!usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_no }}</span>
        </div>
      </div>
      <div *ngIf="usage" class="space-y-8">
        <!-- Method Selection -->
        <div class="border rounded-lg p-4">
          <div class="flex flex-wrap gap-4">
            <!-- Average Method -->
            <div
              (click)="selectMethod('average')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{ 'bg-[#183A25]': selectedMethod === 'average' }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].averageMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].averageMethodDesc }}
                </div>
              </div>
            </div>

            <!-- Asset-Specific Method -->
            <div
              (click)="selectMethod('asset-specific')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{
                  'bg-[#183A25]': selectedMethod === 'asset-specific'
                }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].assetSpecificMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].assetSpecificMethodDesc }}
                </div>
              </div>
            </div>

            <!-- Lessor-Specific Method -->
            <div
              (click)="selectMethod('lessor-specific')"
              class="flex gap-2 items-center cursor-pointer flex-1 min-w-[200px]"
            >
              <div
                [ngClass]="{
                  'bg-[#183A25]': selectedMethod === 'lessor-specific'
                }"
                class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
              ></div>
              <div class="font-medium">
                {{ strings[language].lessorSpecificMethod }}
              </div>
              <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  {{ strings[language].lessorSpecificMethodDesc }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Average Data Method -->
        <div *ngIf="selectedMethod === 'average'" class="space-y-6">
          <!-- Buildings Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].leasedBuildings }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAverageBuildingRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="let control of averageBuildingControls; let i = index"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('average-building', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].buildingType
                    }}</label>
                    <app-dropdown
                      placeholder="{{ strings[language].buildingType }}"
                      [options]="buildingTypeOptions"
                      [control]="control.row.buildingType"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].epcRating
                    }}</label>
                    <app-dropdown
                      placeholder="EPC Rating"
                      [options]="epcRatingOptions"
                      [control]="control.row.epcRating"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>

                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <!-- <app-year-picker (yearChange)="onChange()"></app-year-picker> -->
                    <app-dropdown
                      placeholder="{{ strings[language].select_year }}"
                      [options]="yearOptions"
                      [control]="control.row.reportingYear"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].floorArea
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="m²"
                      [control]="control.row.floorArea"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Vehicles Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].leasedVehicles }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAverageVehicleRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="let control of averageVehicleControls; let i = index"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('average-vehicle', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetCategory
                    }}</label>
                    <app-dropdown
                      placeholder="{{ strings[language].assetCategory }}"
                      [options]="vehicleCategoryOptions"
                      [control]="control.row.assetCategory"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].subtype
                    }}</label>
                    <app-dropdown
                      placeholder="Subtype/Description"
                      [options]="
                        getVehicleSubtypes(control.row.assetCategory.value)
                      "
                      [control]="control.row.subtype"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].quantity
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      [control]="control.row.quantity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].annualDistance
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="km"
                      [control]="control.row.annualDistance"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Asset-Specific Method -->
        <div *ngIf="selectedMethod === 'asset-specific'" class="space-y-6">
          <!-- Buildings Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].buildings }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAssetSpecificBuildingRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of assetSpecificBuildingControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-4">
                  <span>{{ strings[language].asset_Level_Block }}</span>
                  <button
                    (click)="removeRow('asset-specific-building', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">
                      {{ strings[language].subMetered }}
                    </label>
                    <div class="flex items-center">
                      <label class="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          class="sr-only peer"
                          [formControl]="control.row.subMetered"
                          (change)="onToggleChange(control)"
                        />
                        <div
                          class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-[#043B22]/5 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#043B22]"
                        ></div>
                        <span class="ms-3 text-sm font-medium">
                          {{
                            control.row.subMetered.value
                              ? strings[language].subMeteredLabel
                              : strings[language].notSubMetered
                          }}
                        </span>
                      </label>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ strings[language].subMeteredHelp }}
                    </p>
                  </div>
                </div>

                <!-- Energy Entries -->
                <div class="mt-4">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-medium">
                      {{ strings[language].energyEntries }}
                    </h3>
                    <button
                      class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                      (click)="
                        addEnergyEntry(control, control.row.subMetered.value)
                      "
                    >
                      <app-plus></app-plus>
                    </button>
                  </div>

                  <div class="space-y-4">
                    <div
                      *ngFor="
                        let entry of getEnergyEntries(control);
                        let j = index
                      "
                      class="border rounded p-4"
                    >
                      <div class="flex justify-end mb-2">
                        <button
                          (click)="removeEnergyEntry(control, j)"
                          class="text-red-500 text-sm"
                        >
                          <app-trash></app-trash>
                        </button>
                      </div>

                      <div
                        *ngIf="
                          getValue(entry, 'isSubMetered');
                          else nonSubMeteredForm
                        "
                      >
                        <!-- Sub-metered form -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].category
                            }}</label>
                            <app-dropdown
                              placeholder="Category"
                              [options]="energyCategoryOptions"
                              [control]="getFormControl(entry, 'category')"
                              (change)="onCategoryChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].type
                            }}</label>
                            <app-dropdown
                              [placeholder]="getTypePlaceholder(entry)"
                              [options]="
                                getEnergyTypes(
                                  getFormControl(entry, 'category').value
                                )
                              "
                              [control]="getFormControl(entry, 'type')"
                              (change)="onTypeChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].unit
                            }}</label>
                            <app-dropdown
                              [placeholder]="getUnitPlaceholder(entry)"
                              [options]="
                                getEnergyUnits(
                                  getFormControl(entry, 'type').value
                                )
                              "
                              [control]="getFormControl(entry, 'unit')"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].amount
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              [control]="getFormControl(entry, 'amount')"
                              (change)="onChange()"
                            ></app-input>
                          </div>
                        </div>
                      </div>

                      <ng-template #nonSubMeteredForm>
                        <!-- Non-sub-metered form -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].category
                            }}</label>
                            <app-dropdown
                              placeholder="Category"
                              [options]="energyCategoryOptions"
                              [control]="getFormControl(entry, 'category')"
                              (change)="onCategoryChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].type
                            }}</label>
                            <app-dropdown
                              placeholder="{{
                                !getFormControl(entry, 'category').value
                                  ? 'Select a category first'
                                  : 'Select type'
                              }}"
                              [options]="
                                getEnergyTypes(
                                  getFormControl(entry, 'category').value
                                )
                              "
                              [control]="getFormControl(entry, 'type')"
                              (change)="onTypeChange(entry); onChange()"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].unit
                            }}</label>
                            <app-dropdown
                              placeholder="{{
                                !getFormControl(entry, 'type').value
                                  ? 'Select a type first'
                                  : 'Unit will auto-select'
                              }}"
                              [options]="
                                getEnergyUnits(
                                  getFormControl(entry, 'type').value
                                )
                              "
                              [control]="getFormControl(entry, 'unit')"
                            ></app-dropdown>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].totalBuildingEnergy
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              [control]="
                                getFormControl(entry, 'totalBuildingEnergy')
                              "
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].totalBuildingArea
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              unit="m²"
                              [control]="
                                getFormControl(entry, 'totalBuildingArea')
                              "
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].leasedArea
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="0"
                              unit="m²"
                              [control]="getFormControl(entry, 'leasedArea')"
                              (change)="onChange()"
                            ></app-input>
                          </div>

                          <div>
                            <label class="block text-sm font-medium mb-1">{{
                              strings[language].occupancyRate
                            }}</label>
                            <app-input
                              type="number"
                              placeholder="1"
                              min="0"
                              max="1"
                              step="0.1"
                              [control]="getFormControl(entry, 'occupancyRate')"
                              (change)="onChange()"
                            ></app-input>
                          </div>
                        </div>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Vehicles Section -->
          <div>
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].Other_Leased_Assets }}
              </h3>
              <button
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addAssetSpecificVehicleRow()"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div class="space-y-4">
              <div
                *ngFor="
                  let control of assetSpecificVehicleControls;
                  let i = index
                "
                class="border rounded-lg p-4"
              >
                <div class="flex justify-end mb-4 cursor-pointer">
                  <button
                    (click)="removeRow('asset-specific-vehicle', i)"
                    class="text-red-500"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetLabel
                    }}</label>
                    <app-input
                      placeholder="{{
                        strings[language].assetLabelPlaceholder
                      }}"
                      [control]="control.row.assetLabel"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].reportingYear
                    }}</label>
                    <app-year-picker
                      [control]="control.row.reportingYear"
                      (yearChange)="onChange()"
                    ></app-year-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].country
                    }}</label>
                    <app-country-picker
                      [control]="control.row.country"
                      (change)="onChange()"
                    ></app-country-picker>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].assetCategory
                    }}</label>
                    <app-dropdown
                      placeholder="{{ strings[language].assetCategory }}"
                      [options]="getVehicleCategories()"
                      [control]="control.row.assetCategory"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].subtype
                    }}</label>
                    <app-dropdown
                      placeholder="{{ strings[language].subtype }}"
                      [options]="
                        getVehicleSubtypes(control.row.assetCategory.value)
                      "
                      [control]="control.row.subtype"
                      (change)="onChange()"
                    ></app-dropdown>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].quantity
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      [control]="control.row.quantity"
                      (change)="onChange()"
                    ></app-input>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-1">{{
                      strings[language].annualDistance
                    }}</label>
                    <app-input
                      type="number"
                      placeholder="0"
                      unit="km"
                      [control]="control.row.annualDistance"
                      (change)="onChange()"
                    ></app-input>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lessor-Specific Method -->
        <div *ngIf="selectedMethod === 'lessor-specific'" class="space-y-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">
              {{ strings[language].lessorSpecificAssets }}
            </h3>
            <button
              class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
              (click)="addLessorSpecificRow()"
            >
              <app-plus></app-plus>
            </button>
          </div>

          <div class="space-y-4">
            <div
              *ngFor="let control of lessorSpecificControls; let i = index"
              class="border rounded-lg p-4"
            >
              <div class="flex justify-end mb-4 cursor-pointer">
                <button
                  (click)="removeRow('lessor-specific', i)"
                  class="text-red-500"
                >
                  <app-trash></app-trash>
                </button>
              </div>

              <!-- Section A: Identification of the Asset -->
              <h4 class="text-md font-semibold mb-2">
                {{ strings[language].sectionA }}
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].lessorName }}
                  </label>
                  <app-input
                    placeholder="{{ strings[language].lessorNamePlaceholder }}"
                    [control]="control.row.lessorName"
                    (change)="onChange()"
                  ></app-input>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].assetType }}
                  </label>

                  <app-dropdown
                    [options]="lessorAssetTypeOptions"
                    [control]="control.row.assetType"
                    (change)="onChange()"
                    placeholder="{{ strings[language].assetType }}"
                  ></app-dropdown>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].reportingYear }}
                  </label>
                  <app-year-picker
                    [control]="control.row.reportingYear"
                    (yearChange)="onChange()"
                  ></app-year-picker>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].country }}
                  </label>
                  <app-country-picker
                    [control]="control.row.country"
                    (change)="onChange()"
                  ></app-country-picker>
                </div>
              </div>

              <!-- Section B: Emissions Data from Lessor -->
              <h4 class="text-md font-semibold mt-6 mb-2">
                {{ strings[language].sectionB }}
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].scope1Emissions }}
                  </label>
                  <app-input
                    type="number"
                    placeholder="0"
                    unit="kg CO₂e"
                    [control]="control.row.scope1Emissions"
                    (change)="onChange()"
                  ></app-input>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].scope2Emissions }}
                  </label>
                  <app-input
                    type="number"
                    placeholder="0"
                    unit="kg CO₂e"
                    [control]="control.row.scope2Emissions"
                    (change)="onChange()"
                  ></app-input>
                </div>
              </div>

              <!-- Section C: Allocation Basis -->
              <h4 class="text-md font-semibold mt-6 mb-2">
                {{ strings[language].sectionC }}
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].leasedQuantity }}
                  </label>
                  <app-input
                    type="number"
                    placeholder="0"
                    [control]="control.row.leasedQuantity"
                    (change)="onChange()"
                  ></app-input>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].totalQuantity }}
                  </label>
                  <app-input
                    type="number"
                    placeholder="0"
                    [control]="control.row.totalQuantity"
                    (change)="onChange()"
                  ></app-input>
                </div>

                <div *ngIf="control.row.assetType.value === 'building'">
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].occupancyRate }}
                  </label>
                  <app-input
                    type="number"
                    placeholder="1"
                    min="0"
                    max="1"
                    step="0.1"
                    [control]="control.row.occupancyRate"
                    (change)="onChange()"
                  ></app-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  standalone: true
})
export class Category4Question4Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  changeDetectorRef = inject(ChangeDetectorRef);
  usage = false;
  selectedMethod: 'average' | 'asset-specific' | 'lessor-specific' = 'average';

  // Form controls for each method
  averageBuildingControls: { row: AverageDataBuildingRow }[] = [];
  averageVehicleControls: { row: AverageDataVehicleRow }[] = [];
  assetSpecificBuildingControls: {
    row: AssetSpecificBuildingRow;
    energyEntries?: FormGroup[];
  }[] = [];
  assetSpecificVehicleControls: { row: AssetSpecificVehicleRow }[] = [];
  lessorSpecificControls: { row: LessorSpecificRow }[] = [];

  // Dropdown options
  buildingTypes = [
    {
      value: 'office',
      engLabel: 'Office',
      itaLabel: 'Ufficio',
      activityId: 'real_estate-type_office',
      unit: 'm2'
    },
    {
      value: 'distribution-warehouse-warm',
      engLabel: 'Distribution Warehouse - Warm',
      itaLabel: 'Magazzino di distribuzione (non refrigerato)',
      activityId: 'real_estate-type_distribution_warehouse_warm',
      unit: 'm2'
    },
    {
      value: 'distribution-warehouse-cold',
      engLabel: 'Distribution Warehouse - Cold',
      itaLabel: 'Magazzino di distribuzione frigorifero',
      activityId: 'real_estate-type_distribution_warehouse_cold',
      unit: 'm2'
    },
    {
      value: 'retail-high-street',
      engLabel: 'Retail - High Street',
      itaLabel: 'Negozio al dettaglio - strada principale',
      activityId: 'real_estate-type_retail_high_street',
      unit: 'm2'
    },
    {
      value: 'retail-warehouse',
      engLabel: 'Retail - Warehouse',
      itaLabel: 'Negozio al dettaglio - magazzino',
      activityId: 'real_estate-type_retail_warehouse',
      unit: 'm2'
    },
    {
      value: 'retail-shopping-centre',
      engLabel: 'Retail - Shopping Centre',
      itaLabel: 'Negozio al dettaglio - centro commerciale',
      activityId: 'real_estate-type_retail_shopping_center',
      unit: 'm2'
    },
    {
      value: 'hotel',
      engLabel: 'Hotel',
      itaLabel: 'Hotel / Albergo',
      activityId: 'real_estate-type_hotel',
      unit: 'm2'
    },
    {
      value: 'healthcare',
      engLabel: 'Healthcare',
      itaLabel: 'Sanità / Assistenza sanitaria',
      activityId: 'real_estate-type_healthcare',
      unit: 'm2'
    },
    {
      value: 'leisure-lodging',
      engLabel: 'Leisure/Lodging',
      itaLabel: 'Tempo libero o alloggio',
      activityId: 'real_estate-type_leisure_lodging',
      unit: 'm2'
    },
    {
      value: 'non_residential',
      engLabel: 'Non-residential (generic)',
      itaLabel: 'Edificio non residenziale',
      activityId: 'real_estate-type_non_residential_total',
      unit: 'm2'
    }
  ];

  epcRatings = [
    { value: 'a', engLabel: 'A (Outstanding)', itaLabel: 'A (Eccellente)' },
    { value: 'b', engLabel: 'B (Excellent)', itaLabel: 'B (Ottimo)' },
    { value: 'c', engLabel: 'C (Good)', itaLabel: 'C (Buono)' },
    { value: 'd', engLabel: 'D (Average)', itaLabel: 'D (Medio)' },
    { value: 'e', engLabel: 'E (Poor)', itaLabel: 'E (Scarso)' },
    { value: 'f', engLabel: 'F (Very Poor)', itaLabel: 'F (Molto scarso)' },
    { value: 'not-provided', engLabel: 'Not Provided', itaLabel: 'Non fornito' }
  ];

  countries = [
    { value: 'US', engLabel: 'United States', itaLabel: 'Stati Uniti' },
    { value: 'GB', engLabel: 'United Kingdom', itaLabel: 'Regno Unito' },
    { value: 'IT', engLabel: 'Italy', itaLabel: 'Italia' },
    { value: 'DE', engLabel: 'Germany', itaLabel: 'Germania' },
    { value: 'FR', engLabel: 'France', itaLabel: 'Francia' },
    { value: 'ES', engLabel: 'Spain', itaLabel: 'Spagna' }
  ];

  vehicleCategories = [
    {
      value: 'Vehicles_and_trucks',
      engLabel: 'Vehicles and trucks',
      itaLabel: 'Veicoli e camion'
    }
  ];

  vehicleSubtypes: Record<string, any[]> = {
    Vehicles_and_trucks: [
      {
        value: 'passenger-car-small',
        engLabel: 'Passenger car – small',
        itaLabel: 'Auto passeggeri – piccola'
      },
      {
        value: 'passenger-car-medium',
        engLabel: 'Passenger car – medium',
        itaLabel: 'Auto passeggeri – media'
      },
      {
        value: 'light-van',
        engLabel: 'Light van',
        itaLabel: 'Furgone leggero'
      },
      {
        value: 'rigid-truck',
        engLabel: 'Rigid truck',
        itaLabel: 'Camion rigido'
      },
      {
        value: 'articulated-truck',
        engLabel: 'Articulated truck',
        itaLabel: 'Camion articolato'
      },
      {
        value: 'electric-car',
        engLabel: 'Electric car',
        itaLabel: 'Auto elettrica'
      },
      {
        value: 'plug-in-hybrid-car',
        engLabel: 'Plug-in hybrid car',
        itaLabel: 'Auto ibrida plug-in'
      },
      {
        value: 'motorcycle-scooter',
        engLabel: 'Motorcycle / scooter',
        itaLabel: 'Moto / scooter'
      }
    ]
  };

  lessorAssetTypes = [
    { value: 'building', engLabel: 'Building', itaLabel: 'Edificio' },
    { value: 'vehicle', engLabel: 'Vehicle', itaLabel: 'Veicolo' },
    { value: 'machinery', engLabel: 'Machinery', itaLabel: 'Macchinario' },
    { value: 'other', engLabel: 'Other', itaLabel: 'Altro' }
  ];

  energyTypes: Record<string, any[]> = {
    'energy-fuel': [
      {
        value: 'electricity',
        engLabel: 'Electricity',
        itaLabel: 'Elettricità',
        units: ['kWh', 'MWh']
      },
      {
        value: 'natural-gas',
        engLabel: 'Natural Gas',
        itaLabel: 'Gas naturale',
        units: ['m³']
      },
      {
        value: 'diesel',
        engLabel: 'Diesel',
        itaLabel: 'Diesel',
        units: ['liters']
      },
      {
        value: 'heating-oil',
        engLabel: 'Heating Oil',
        itaLabel: 'Olio combustibile',
        units: ['liters']
      }
    ]
  };

  strings: any = {
    english: {
      choice_yes: 'Yes',
      choice_no: 'No',
      answer: 'Please fill in the required data if yes to proceed.',
      selectMethod: 'Select Calculation Method',
      averageMethod: 'Average-Data Method',
      averageMethodDesc:
        'Use industry-average emission factors based on asset size/count',
      assetSpecificMethod: 'Asset-Specific Method',
      assetSpecificMethodDesc:
        'Use actual energy/fuel consumption data for each asset',
      lessorSpecificMethod: 'Lessor-Specific Method',
      lessorSpecificMethodDesc:
        'Use Scope 1+2 emissions data provided by the lessor',
      leasedBuildings: 'Leased Buildings & Real-Estate',
      leasedVehicles: 'Other Leased Assets',
      lessorSpecificAssets: 'Leased Assets (Lessor-Specific)',
      assetLabel: 'Asset Label',
      assetLabelPlaceholder: 'e.g., Milan HQ - 3rd floor',
      buildingType: 'Building Type',
      epcRating: 'EPC Rating',
      country: 'Country/Region',
      reportingYear: 'Reporting Year',
      floorArea: 'Floor Area',
      assetCategory: 'Asset Category',
      subtype: 'Subtype/Description',
      quantity: 'Quantity',
      annualDistance: 'Annual Distance',
      subMetered: 'Sub-metered Data?',
      subMeteredHelp:
        'Energy/fuel used by your leased space is measured separately',
      energyEntries: 'Energy/Emission Entries (Repeatable Inside Asset)',
      category: 'Category',
      type: 'Type',
      unit: 'Unit of Measure',
      amount: 'Amount Consumed',
      totalBuildingEnergy: 'Total Building Energy Use',
      totalBuildingArea: 'Total Building Area',
      leasedArea: 'Leased Area',
      occupancyRate: 'Occupancy Rate',
      addEnergyEntry: 'Add Energy Entry',
      lessorName: 'Lessor Name',
      lessorNamePlaceholder: 'Name of the asset owner',
      assetType: 'Asset Type',
      scope1Emissions: 'Scope 1 Emissions',
      scope2Emissions: 'Scope 2 Emissions',
      leasedQuantity: 'Leased Quantity',
      totalQuantity: 'Total Quantity',
      select_year: 'Select year',
      subMeteredLabel: 'Sub-metered',
      notSubMetered: 'Not Sub-metered',
      sectionA: 'Section A: Identification of the Asset',
      sectionB: 'Section B: Emissions Data from Lessor',
      sectionC: 'Section C: Allocation Basis',
      buildings: 'A. Buildings',
      Other_Leased_Assets: 'B. Other assets (Vehicles)',
      asset_Level_Block: 'Asset Level Block'
    },
    italian: {
      choice_yes: 'Sì',
      choice_no: 'No',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      selectMethod: 'Seleziona Metodo di Calcolo',
      averageMethod: 'Metodo Dati Medi',
      averageMethodDesc:
        'Utilizza fattori di emissione medi del settore basati su dimensioni/conteggio degli asset',
      assetSpecificMethod: 'Metodo Specifico per Asset',
      assetSpecificMethodDesc:
        'Utilizza dati reali di consumo energetico/carburante per ogni asset',
      lessorSpecificMethod: 'Metodo Specifico del Locatore',
      lessorSpecificMethodDesc:
        'Utilizza dati di emissioni Scope 1+2 forniti dal locatore',
      leasedBuildings: 'Edifici e immobili in leasing',
      leasedVehicles: 'Altri beni in leasing',
      lessorSpecificAssets: 'Asset in Locazione (Specifico Locatore)',
      assetLabel: 'Etichetta Asset',
      assetLabelPlaceholder: 'es. Sede Milano - 3° piano',
      buildingType: 'Tipo di Edificio',
      epcRating: 'Classe Energetica',
      country: 'Paese/Regione',
      reportingYear: 'Anno di Riferimento',
      floorArea: 'Superficie',
      assetCategory: 'Categoria Asset',
      subtype: 'Sottotipo/Descrizione',
      quantity: 'Quantità',
      annualDistance: 'Distanza Annuale',
      subMetered: 'Dati Sub-misurati?',
      subMeteredHelp:
        "L'energia/carburante utilizzato dal tuo spazio in locazione è misurato separatamente",
      energyEntries:
        "Voci di energia/emissioni (ripetibili all'interno della risorsa)",
      category: 'Categoria',
      type: 'Tipo',
      unit: 'Unità di Misura',
      amount: 'Quantità Consumata',
      totalBuildingEnergy: 'Consumo Energetico Totale Edificio',
      totalBuildingArea: 'Superficie Totale Edificio',
      leasedArea: 'Superficie in Locazione',
      occupancyRate: 'Tasso di Occupazione',
      addEnergyEntry: 'Aggiungi Voce Energia',
      lessorName: 'Nome Locatore',
      lessorNamePlaceholder: "Nome del proprietario dell'asset",
      assetType: 'Tipo di Asset',
      scope1Emissions: 'Emissioni Scope 1',
      scope2Emissions: 'Emissioni Scope 2',
      leasedQuantity: 'Quantità Locata',
      totalQuantity: 'Quantità Totale',
      select_year: 'Seleziona anno',
      subMeteredLabel: 'Sotto-contatore',
      notSubMetered: 'Non sotto-contatore',
      sectionA: 'Sezione A: Identificazione del Bene',
      sectionB: 'Sezione B: Dati sulle Emissioni dal Locatore',
      sectionC: 'Sezione C: Criterio di Allocazione',
      buildings: 'A. Edifici',
      Other_Leased_Assets: 'B. Altri beni (Veicoli)',
      asset_Level_Block: 'Blocco a livello di risorsa'
    }
  };
  PRODUCT_CATEGORIES: any[] = [
    {
      name: 'Energy and Fuel',
      itaName: 'Energia e carburanti',
      subcategories: [
        {
          id: 'electricity-supply_grid-source_total_supplier_mix',
          label: 'Electricity from grid',
          itaLabel: 'Elettricità dalla rete'
        },
        {
          id: 'fuel-type_natural_gas-fuel_use_na',
          label: 'Natural gas',
          itaLabel: 'Gas naturale'
        },
        {
          id: 'fuel-type_diesel-fuel_use_na',
          label: 'Diesel',
          itaLabel: 'Gasolio'
        },
        {
          id: 'fuel-type_heating_oil-fuel_use_stationary',
          label: 'Heating oil',
          itaLabel: 'Olio combustibile'
        }
      ]
    },
    {
      name: 'Refrigerant gas for air conditioning',
      itaName: 'Gas refrigeranti per aria condizionata',
      subcategories: [
        {
          id: 'fugitive_gas-type_hfc_23',
          label: 'HFC-23',
          itaLabel: 'HFC-23'
        },
        {
          id: 'fugitive_gas-type_hfc_236fa',
          label: 'HFC-236fa',
          itaLabel: 'HFC-236fa'
        },
        {
          id: 'fugitive_gas-type_hfc_143a',
          label: 'HFC-143a',
          itaLabel: 'HFC-143a'
        },
        {
          id: 'fugitive_gas-type_hfc_32',
          label: 'HFC-32',
          itaLabel: 'HFC-32'
        },
        {
          id: 'fugitive_gas-type_hfc_134a',
          label: 'HFC-134a',
          itaLabel: 'HFC-134a'
        },
        {
          id: 'fugitive_gas-type_hfc_227ea',
          label: 'HFC-227ea',
          itaLabel: 'HFC-227ea'
        },
        {
          id: 'fugitive_gas-type_hfc_125',
          label: 'HFC-125',
          itaLabel: 'HFC-125'
        },
        {
          id: 'fugitive_gas-type_r744',
          label: 'R-744 (CO₂)',
          itaLabel: 'R-744 (CO₂)'
        },
        {
          id: 'fugitive_gas-type_r290_propane',
          label: 'R-290 (Propane)',
          itaLabel: 'R-290 (Propano)'
        },
        {
          id: 'fugitive_gas-type_hcfc_22r22_chlorodifluoromethane',
          label: 'R-22',
          itaLabel: 'R-22'
        }
      ]
    },
    {
      name:
        'Gas for refrigeration systems (e.g., cold storage, commercial refrigeration)',
      itaName:
        'Gas per sistemi di refrigerazione (es. celle frigorifere, refrigerazione commerciale)',
      subcategories: [
        {
          id: 'fugitive_gas-type_hfc_23',
          label: 'HFC-23',
          itaLabel: 'HFC-23'
        },
        {
          id: 'fugitive_gas-type_hfc_236fa',
          label: 'HFC-236fa',
          itaLabel: 'HFC-236fa'
        },
        {
          id: 'fugitive_gas-type_hfc_143a',
          label: 'HFC-143a',
          itaLabel: 'HFC-143a'
        },
        {
          id: 'fugitive_gas-type_hfc_134a',
          label: 'HFC-134a',
          itaLabel: 'HFC-134a'
        },
        {
          id: 'fugitive_gas-type_hfc_125',
          label: 'HFC-125',
          itaLabel: 'HFC-125'
        },
        {
          id: 'fugitive_gas-type_hfc_152a',
          label: 'HFC-152a',
          itaLabel: 'HFC-152a'
        },
        {
          id: 'fugitive_gas-type_hfc_227ea',
          label: 'HFC-227ea',
          itaLabel: 'HFC-227ea'
        },
        {
          id: 'fugitive_gas-type_r507a',
          label: 'R-507A',
          itaLabel: 'R-507A'
        },
        {
          id: 'fugitive_gas-type_r744',
          label: 'R-744 (CO₂)',
          itaLabel: 'R-744 (CO₂)'
        },
        {
          id: 'fugitive_gas-type_r717',
          label: 'R-717 (Ammonia, NH₃)',
          itaLabel: 'R-717 (Ammoniaca, NH₃)'
        },
        {
          id: 'fugitive_gas-type_r404a',
          label: 'R-404A',
          itaLabel: 'R-404A'
        }
      ]
    }
  ];
  yearOptions = [
    { label: '2020', value: 2020 },
    { label: '2021', value: 2021 },
    { label: '2022', value: 2022 },
    { label: '2023', value: 2023 },
    { label: '2024', value: 2024 },
    { label: '2025', value: 2025 }
  ];

  ngOnInit() {
    this.loadSavedData();
    this.initializeDefaultRows();
  }

  initializeDefaultRows() {
    if (this.averageBuildingControls.length === 0) {
      this.addAverageBuildingRow();
    }
    if (this.averageVehicleControls.length === 0) {
      this.addAverageVehicleRow();
    }
    if (this.assetSpecificBuildingControls.length === 0) {
      this.addAssetSpecificBuildingRow();
    }
    if (this.assetSpecificVehicleControls.length === 0) {
      this.addAssetSpecificVehicleRow();
    }
    if (this.lessorSpecificControls.length === 0) {
      this.addLessorSpecificRow();
    }
  }

  loadSavedData() {
    const savedData = this.getSavedData();
    if (savedData) {
      this.usage = savedData.usage || false;
      this.selectedMethod = savedData.selectedMethod || 'average';
      this.selectedMethod = savedData.selectedMethod || 'average';

      // Load average building data
      if (savedData.averageBuildingControls) {
        this.averageBuildingControls = savedData.averageBuildingControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              buildingType: new FormControl(
                item.buildingType,
                Validators.required
              ),
              epcRating: new FormControl(item.epcRating),
              country: new FormControl(item.country, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              floorArea: new FormControl(item.floorArea, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('building') as FormControl<'building'>
            }
          })
        );
      }

      // Load average vehicle data
      if (savedData.averageVehicleControls) {
        this.averageVehicleControls = savedData.averageVehicleControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              country: new FormControl(item.country),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              quantity: new FormControl(item.quantity, [
                Validators.required,
                Validators.min(0)
              ]),
              annualDistance: new FormControl(item.annualDistance, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('average') as FormControl<'average'>,
              assetType: new FormControl('vehicle') as FormControl<'vehicle'>
            }
          })
        );
      }

      // Load asset-specific building data
      if (savedData.assetSpecificBuildingControls) {
        this.assetSpecificBuildingControls = savedData.assetSpecificBuildingControls.map(
          (item: any) => {
            const control = {
              row: {
                assetLabel: new FormControl(
                  item.assetLabel,
                  Validators.required
                ),
                reportingYear: new FormControl(
                  item.reportingYear,
                  Validators.required
                ),
                country: new FormControl(item.country, Validators.required),
                subMetered: new FormControl(
                  item.subMetered,
                  Validators.required
                ),
                method: new FormControl('asset-specific') as FormControl<
                  'asset-specific'
                >,
                assetType: new FormControl('building') as FormControl<
                  'building'
                >
              },
              energyEntries: [] as FormGroup[]
            };

            if (item.energyEntries) {
              control.energyEntries = item.energyEntries.map((entry: any) => {
                if (entry.isSubMetered) {
                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    amount: new FormControl(entry.amount, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    isSubMetered: new FormControl(true, { nonNullable: true })
                  }) as FormGroup<SubMeteredEntry>;
                } else {
                  return new FormGroup({
                    category: new FormControl(
                      entry.category,
                      Validators.required
                    ),
                    type: new FormControl(entry.type, Validators.required),
                    unit: new FormControl(entry.unit, Validators.required),
                    totalBuildingEnergy: new FormControl(
                      entry.totalBuildingEnergy,
                      [Validators.required, Validators.min(0)]
                    ),
                    totalBuildingArea: new FormControl(
                      entry.totalBuildingArea,
                      [Validators.required, Validators.min(0)]
                    ),
                    leasedArea: new FormControl(entry.leasedArea, [
                      Validators.required,
                      Validators.min(0)
                    ]),
                    occupancyRate: new FormControl(entry.occupancyRate || 1, [
                      Validators.required,
                      Validators.min(0),
                      Validators.max(1)
                    ]),
                    isSubMetered: new FormControl(false, { nonNullable: true })
                  }) as FormGroup<NonSubMeteredEntry>;
                }
              });
            }

            return control;
          }
        );
      }

      // Load asset-specific vehicle data
      if (savedData.assetSpecificVehicleControls) {
        this.assetSpecificVehicleControls = savedData.assetSpecificVehicleControls.map(
          (item: any) => ({
            row: {
              assetLabel: new FormControl(item.assetLabel),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country),
              assetCategory: new FormControl(
                item.assetCategory,
                Validators.required
              ),
              subtype: new FormControl(item.subtype, Validators.required),
              quantity: new FormControl(item.quantity, [
                Validators.required,
                Validators.min(0)
              ]),
              annualDistance: new FormControl(item.annualDistance, [
                Validators.required,
                Validators.min(0)
              ]),
              method: new FormControl('asset-specific') as FormControl<
                'asset-specific'
              >,
              assetType: new FormControl('vehicle') as FormControl<'vehicle'>
            }
          })
        );
      }

      // Load lessor-specific data
      if (savedData.lessorSpecificControls) {
        this.lessorSpecificControls = savedData.lessorSpecificControls.map(
          (item: any) => ({
            row: {
              lessorName: new FormControl(item.lessorName),
              assetType: new FormControl(item.assetType, Validators.required),
              reportingYear: new FormControl(
                item.reportingYear,
                Validators.required
              ),
              country: new FormControl(item.country, Validators.required),
              scope1Emissions: new FormControl(item.scope1Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              scope2Emissions: new FormControl(item.scope2Emissions, [
                Validators.required,
                Validators.min(0)
              ]),
              leasedQuantity: new FormControl(item.leasedQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              totalQuantity: new FormControl(item.totalQuantity, [
                Validators.required,
                Validators.min(0)
              ]),
              occupancyRate: new FormControl(item.occupancyRate || 1, [
                Validators.min(0),
                Validators.max(1)
              ]),
              method: new FormControl('lessor-specific') as FormControl<
                'lessor-specific'
              >
            }
          })
        );
      }
    }
  }

  getSavedData(): any {
    const categories = localStorage.getItem('categories');
    if (categories) {
      const parsed = JSON.parse(categories);
      return parsed?.[3]?.questions?.[3]?.answers || null;
    }
    return null;
  }

  selectMethod(method: 'average' | 'asset-specific' | 'lessor-specific') {
    this.selectedMethod = method;
    this.onChange();
  }

  // Average Data Method
  addAverageBuildingRow() {
    this.averageBuildingControls.push({
      row: {
        assetLabel: new FormControl(''),
        buildingType: new FormControl('', Validators.required),
        epcRating: new FormControl(''),
        country: new FormControl('', Validators.required),
        reportingYear: new FormControl('', Validators.required),
        floorArea: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('building') as FormControl<'building'>
      }
    });
    this.onChange();
  }

  addAverageVehicleRow() {
    this.averageVehicleControls.push({
      row: {
        assetLabel: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        country: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        quantity: new FormControl('', [Validators.required, Validators.min(0)]),
        annualDistance: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('average') as FormControl<'average'>,
        assetType: new FormControl('vehicle') as FormControl<'vehicle'>
      }
    });
    this.onChange();
  }

  // Asset-Specific Method
  addAssetSpecificBuildingRow() {
    const newControl = {
      row: {
        assetLabel: new FormControl<string | null>('', Validators.required),
        reportingYear: new FormControl<string | null>('', Validators.required),
        country: new FormControl<string | null>('', Validators.required),
        subMetered: new FormControl(true, {
          nonNullable: true,
          validators: Validators.required
        }),
        method: new FormControl<'asset-specific'>('asset-specific', {
          nonNullable: true
        }),
        assetType: new FormControl<'building'>('building', {
          nonNullable: true
        })
      },
      energyEntries: [] as FormGroup[]
    };

    this.assetSpecificBuildingControls.push(newControl);
    this.addEnergyEntry(newControl, true);
    this.onChange();
  }

  addAssetSpecificVehicleRow() {
    this.assetSpecificVehicleControls.push({
      row: {
        assetLabel: new FormControl(''),
        reportingYear: new FormControl('', Validators.required),
        country: new FormControl(''),
        assetCategory: new FormControl('', Validators.required),
        subtype: new FormControl('', Validators.required),
        quantity: new FormControl('', [Validators.required, Validators.min(0)]),
        annualDistance: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        method: new FormControl('asset-specific') as FormControl<
          'asset-specific'
        >,
        assetType: new FormControl('vehicle') as FormControl<'vehicle'>
      }
    });
    this.onChange();
  }

  addEnergyEntry(
    control: { row: AssetSpecificBuildingRow; energyEntries?: EnergyEntry[] },
    isSubMetered: boolean
  ) {
    if (!control.energyEntries) {
      control.energyEntries = [];
    }

    if (isSubMetered) {
      const newEntry = new FormGroup<SubMeteredEntry>({
        category: new FormControl<string | null>('', Validators.required),
        type: new FormControl<string | null>('', Validators.required),
        unit: new FormControl<string | null>('', Validators.required),
        amount: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        isSubMetered: new FormControl<true>(true, { nonNullable: true })
      });
      control.energyEntries.push(newEntry);
    } else {
      const newEntry = new FormGroup<NonSubMeteredEntry>({
        category: new FormControl<string | null>('', Validators.required),
        type: new FormControl<string | null>('', Validators.required),
        unit: new FormControl<string | null>('', Validators.required),
        totalBuildingEnergy: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalBuildingArea: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        leasedArea: new FormControl<string | null>('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl<any>(1, [
          Validators.required,
          Validators.min(0),
          Validators.max(1)
        ]),
        isSubMetered: new FormControl<false>(false, { nonNullable: true })
      });
      control.energyEntries.push(newEntry);
    }

    this.onChange();
  }

  removeEnergyEntry(
    control: { row: AssetSpecificBuildingRow; energyEntries?: FormGroup[] },
    index: number
  ) {
    if (control.energyEntries && control.energyEntries.length > index) {
      control.energyEntries.splice(index, 1);
      this.onChange();
    }
  }

  getEnergyEntries(control: {
    row: AssetSpecificBuildingRow;
    energyEntries?: EnergyEntry[];
  }): EnergyEntry[] {
    return control.energyEntries || [];
  }

  // Lessor-Specific Method
  addLessorSpecificRow() {
    this.lessorSpecificControls.push({
      row: {
        lessorName: new FormControl(''),
        assetType: new FormControl('', Validators.required),
        reportingYear: new FormControl('', Validators.required),
        country: new FormControl('', Validators.required),
        scope1Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        scope2Emissions: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        leasedQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        totalQuantity: new FormControl('', [
          Validators.required,
          Validators.min(0)
        ]),
        occupancyRate: new FormControl(1, [
          Validators.min(0),
          Validators.max(1)
        ]) as FormControl<number>,
        method: new FormControl('lessor-specific') as FormControl<
          'lessor-specific'
        >
      }
    });
    this.onChange();
  }

  // Helper methods
  getVehicleSubtypes(category: string | null): any[] {
    if (!category) return [];
    return (this.vehicleSubtypes[category] || []).map(s => ({
      ...s,
      label: this.language === 'english' ? s.engLabel : s.itaLabel
    }));
  }

  // Define unit mappings for specific types
  getEnergyUnits(typeId: string | null): any[] {
    if (!typeId) {
      return [
        {
          value: null,
          label:
            this.language === 'italian'
              ? 'Seleziona un tipo prima'
              : 'Select a type first',
          disabled: true
        }
      ];
    }

    // Define unit mappings for specific types
    const unitMappings: Record<string, any[]> = {
      // Electricity types
      'electricity-supply_grid-source_total_supplier_mix': [
        { value: 'kWh', label: 'kWh' },
        { value: 'MWh', label: 'MWh' }
      ],
      // Fuel types
      'fuel-type_natural_gas-fuel_use_na': [{ value: 'm³', label: 'm³' }],
      'fuel-type_diesel-fuel_use_na': [{ value: 'liters', label: 'liters' }],
      'fuel-type_heating_oil-fuel_use_stationary': [
        { value: 'liters', label: 'liters' }
      ],
      // Refrigerant gases - ALL USE m³ INSTEAD OF kg
      'fugitive_gas-type_hfc_23': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_236fa': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_143a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_32': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_134a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_227ea': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hfc_125': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r744': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r290_propane': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_hcfc_22r22_chlorodifluoromethane': [
        { value: 'm³', label: 'm³' }
      ],
      'fugitive_gas-type_hfc_152a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r507a': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r717': [{ value: 'm³', label: 'm³' }],
      'fugitive_gas-type_r404a': [{ value: 'm³', label: 'm³' }]
    };

    return unitMappings[typeId];
  }
  getFormControl(entry: AbstractControl, controlName: string): FormControl {
    return (entry as FormGroup).get(controlName) as FormControl;
  }
  getValue(entry: AbstractControl, controlName: string): any {
    return (entry as FormGroup).get(controlName)?.value;
  }

  onToggleChange(control: {
    row: AssetSpecificBuildingRow;
    energyEntries?: FormGroup[];
  }) {
    // Clear existing energy entries when toggle changes
    control.energyEntries = [];

    // Add a new entry with the correct form structure
    this.addEnergyEntry(control, control.row.subMetered.value);

    // Force change detection
    this.changeDetectorRef.detectChanges();
  }

  // Remove rows
  removeRow(
    type:
      | 'average-building'
      | 'average-vehicle'
      | 'asset-specific-building'
      | 'asset-specific-vehicle'
      | 'lessor-specific',
    index: number
  ) {
    switch (type) {
      case 'average-building':
        if (this.averageBuildingControls.length > index) {
          this.averageBuildingControls.splice(index, 1);
        }
        break;
      case 'average-vehicle':
        if (this.averageVehicleControls.length > index) {
          this.averageVehicleControls.splice(index, 1);
        }
        break;
      case 'asset-specific-building':
        if (this.assetSpecificBuildingControls.length > index) {
          this.assetSpecificBuildingControls.splice(index, 1);
        }
        break;
      case 'asset-specific-vehicle':
        if (this.assetSpecificVehicleControls.length > index) {
          this.assetSpecificVehicleControls.splice(index, 1);
        }
        break;
      case 'lessor-specific':
        if (this.lessorSpecificControls.length > index) {
          this.lessorSpecificControls.splice(index, 1);
        }
        break;
    }
    this.onChange();
  }

  onChange = () => {
    if (this.categories && this.categories.length > 3) {
      let dataToSave: any = {
        usage: this.usage,
        selectedMethod: this.selectedMethod
      };

      // Save only the selected method's data
      if (this.selectedMethod === 'average') {
        dataToSave.averageBuildingControls = this.averageBuildingControls.map(
          control => {
            const buildingType = this.buildingTypes.find(
              b => b.value === control.row.buildingType.value
            );
            const epcRating = this.epcRatings.find(
              e => e.value === control.row.epcRating.value
            );

            return {
              assetLabel: control.row.assetLabel.value,
              buildingType: control.row.buildingType.value,
              buildingType_info: buildingType
                ? {
                  value: buildingType.value,
                  label: buildingType.engLabel,
                  itaLabel: buildingType.itaLabel,
                  activityId: buildingType.activityId,
                  unit: buildingType.unit
                }
                : null,
              epcRating: control.row.epcRating.value,
              epcRating_info: epcRating
                ? {
                  value: epcRating.value,
                  label: epcRating.engLabel,
                  itaLabel: epcRating.itaLabel
                }
                : null,
              country: control.row.country.value,
              reportingYear: control.row.reportingYear.value,
              floorArea: control.row.floorArea.value,
              method: control.row.method.value,
              assetType: control.row.assetType.value
            };
          }
        );

        dataToSave.averageVehicleControls = this.averageVehicleControls.map(
          control => {
            const assetCategory = this.vehicleCategories.find(
              v => v.value === control.row.assetCategory.value
            );
            const subtype = this.getVehicleSubtypes(
              control.row.assetCategory.value
            ).find(s => s.value === control.row.subtype.value);

            return {
              assetLabel: control.row.assetLabel.value,
              assetCategory: control.row.assetCategory.value,
              assetCategory_info: assetCategory
                ? {
                  value: assetCategory.value,
                  label: assetCategory.engLabel,
                  itaLabel: assetCategory.itaLabel
                }
                : null,
              subtype: control.row.subtype.value,
              subtype_info: subtype
                ? {
                  value: subtype.value,
                  label: subtype.engLabel,
                  itaLabel: subtype.itaLabel
                }
                : null,
              country: control.row.country.value,
              reportingYear: control.row.reportingYear.value,
              quantity: control.row.quantity.value,
              annualDistance: control.row.annualDistance.value,
              method: control.row.method.value,
              assetType: control.row.assetType.value
            };
          }
        );
      } else if (this.selectedMethod === 'asset-specific') {
        dataToSave.assetSpecificBuildingControls = this.assetSpecificBuildingControls.map(
          control => ({
            assetLabel: control.row.assetLabel.value,
            reportingYear: control.row.reportingYear.value,
            country: control.row.country.value,
            subMetered: control.row.subMetered.value,
            method: control.row.method.value,
            assetType: control.row.assetType.value,
            energyEntries:
              control.energyEntries?.map(entry => {
                const entryValue = entry.value;
                const category = this.PRODUCT_CATEGORIES.find(
                  c => c.name === entryValue.category
                );
                const subcategory = category?.subcategories?.find(
                  (s: any) => s.id === entryValue.type
                );

                return {
                  ...entryValue,
                  category_info: category
                    ? {
                      name: category.name,
                      label: category.label,
                      itaLabel: category.itaLabel,
                      itaName: category.itaName
                    }
                    : null,
                  type_info: subcategory
                    ? {
                      id: subcategory.id,
                      label: subcategory.label,
                      itaLabel: subcategory.itaLabel
                    }
                    : null
                };
              }) || []
          })
        );

        dataToSave.assetSpecificVehicleControls = this.assetSpecificVehicleControls.map(
          control => {
            const assetCategory = this.vehicleCategories.find(
              v => v.value === control.row.assetCategory.value
            );
            const subtype = this.getVehicleSubtypes(
              control.row.assetCategory.value
            ).find(s => s.value === control.row.subtype.value);

            return {
              assetLabel: control.row.assetLabel.value,
              reportingYear: control.row.reportingYear.value,
              country: control.row.country.value,
              assetCategory: control.row.assetCategory.value,
              assetCategory_info: assetCategory
                ? {
                  value: assetCategory.value,
                  label: assetCategory.engLabel,
                  itaLabel: assetCategory.itaLabel
                }
                : null,
              subtype: control.row.subtype.value,
              subtype_info: subtype
                ? {
                  value: subtype.value,
                  label: subtype.engLabel,
                  itaLabel: subtype.itaLabel
                }
                : null,
              quantity: control.row.quantity.value,
              annualDistance: control.row.annualDistance.value,
              method: control.row.method.value,
              assetType: control.row.assetType.value
            };
          }
        );
      } else if (this.selectedMethod === 'lessor-specific') {
        dataToSave.lessorSpecificControls = this.lessorSpecificControls.map(
          control => {
            const assetType = this.lessorAssetTypes.find(
              l => l.value === control.row.assetType.value
            );

            return {
              lessorName: control.row.lessorName.value,
              assetType: control.row.assetType.value,
              assetType_info: assetType
                ? {
                  value: assetType.value,
                  label: assetType.engLabel,
                  itaLabel: assetType.itaLabel
                }
                : null,
              reportingYear: control.row.reportingYear.value,
              country: control.row.country.value,
              scope1Emissions: control.row.scope1Emissions.value,
              scope2Emissions: control.row.scope2Emissions.value,
              leasedQuantity: control.row.leasedQuantity.value,
              totalQuantity: control.row.totalQuantity.value,
              occupancyRate: control.row.occupancyRate.value,
              method: control.row.method.value
            };
          }
        );
      }

      // Store only selected method data
      if (!this.categories[3].questions[3]) {
        this.categories[3].questions[3] = { answers: {} } as any;
      }
      this.categories[3].questions[3].answers = dataToSave;

      this.categoryService.persistCategories(this.categories);
    }
  };

  private _energyCategoryCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get energyCategoryOptions(): { value: string; label: string }[] {
    const lang = this.language;
    if (this._energyCategoryCache?.lang === lang) {
      return this._energyCategoryCache.opts;
    }
    const opts = this.PRODUCT_CATEGORIES.map(category => ({
      value: category.name,
      label: lang === 'italian' ? category.itaName : category.name
    }));
    this._energyCategoryCache = { lang, opts };
    return opts;
  }
  onTypeChange(entry: FormGroup) {
    const typeValue = entry.get('type')?.value;
    const unitControl = entry.get('unit');

    if (typeValue && unitControl) {
      // Get the first available unit for this type
      const units = this.getEnergyUnits(typeValue);
      if (units.length > 0) {
        unitControl.setValue(units[0].value);
      }
    }

    this.onChange();
  }
  getEnergyTypes(categoryName: string | null): any[] {
    if (!categoryName) {
      return [
        {
          value: null,
          label:
            this.language === 'italian'
              ? 'Seleziona prima una categoria'
              : 'Select a category first',
          disabled: true
        }
      ];
    }

    const category = this.PRODUCT_CATEGORIES.find(c => c.name === categoryName);
    if (!category || !category.subcategories) return [];

    return category.subcategories.map((subcategory: any) => ({
      value: subcategory.id,
      label:
        this.language === 'italian' ? subcategory.itaLabel : subcategory.label
    }));
  }
  onCategoryChange(entry: FormGroup) {
    // Reset the type and unit controls when category changes
    entry.get('type')?.reset();
    entry.get('unit')?.reset();
    this.onChange();
  }
  getTypePlaceholder(entry: any): string {
    if (!this.getFormControl(entry, 'category').value) {
      return this.language === 'english'
        ? 'Select a category first'
        : 'Seleziona prima una categoria';
    } else {
      return this.language === 'english' ? 'Select type' : 'Seleziona il tipo';
    }
  }

  getUnitPlaceholder(entry: any): string {
    if (!this.getFormControl(entry, 'type').value) {
      return this.language === 'english'
        ? 'Select a type first'
        : 'Seleziona prima un tipo';
    } else {
      return this.language === 'english'
        ? 'Unit will auto-select'
        : "L'unità verrà selezionata automaticamente";
    }
  }
  toggleUsage(value: boolean): void {
    this.usage = value;

    if (!value) {
      // Clear all form data when switching to "No"
      this.averageBuildingControls = [];
      this.averageVehicleControls = [];
      this.assetSpecificBuildingControls = [];
      this.assetSpecificVehicleControls = [];
      this.lessorSpecificControls = [];
    } else {
      // Initialize default rows when switching to "Yes"
      this.initializeDefaultRows();
    }

    this.onChange();
  }
  private _buildingTypeCache: { lang: string; opts: any[] } | null = null;
  get buildingTypeOptions() {
    const lang = this.language;
    if (this._buildingTypeCache?.lang === lang) return this._buildingTypeCache.opts;
    const opts = this.buildingTypes.map(b => ({ ...b, label: lang === 'english' ? b.engLabel : b.itaLabel }));
    this._buildingTypeCache = { lang, opts };
    return opts;
  }

  private _epcRatingCache: { lang: string; opts: any[] } | null = null;
  get epcRatingOptions() {
    const lang = this.language;
    if (this._epcRatingCache?.lang === lang) return this._epcRatingCache.opts;
    const opts = this.epcRatings.map(r => ({ ...r, label: lang === 'english' ? r.engLabel : r.itaLabel }));
    this._epcRatingCache = { lang, opts };
    return opts;
  }

  private _vehicleCategoryCache: { lang: string; opts: any[] } | null = null;
  get vehicleCategoryOptions() {
    const lang = this.language;
    if (this._vehicleCategoryCache?.lang === lang) return this._vehicleCategoryCache.opts;
    const opts = this.vehicleCategories.map(v => ({ ...v, label: lang === 'english' ? v.engLabel : v.itaLabel }));
    this._vehicleCategoryCache = { lang, opts };
    return opts;
  }

  private _lessorAssetCache: { lang: string; opts: any[] } | null = null;
  get lessorAssetTypeOptions() {
    const lang = this.language;
    if (this._lessorAssetCache?.lang === lang) return this._lessorAssetCache.opts;
    const opts = this.lessorAssetTypes.map(l => ({ ...l, label: lang === 'english' ? l.engLabel : l.itaLabel }));
    this._lessorAssetCache = { lang, opts };
    return opts;
  }
  getVehicleCategories() {
    return this.vehicleCategories.map(v => ({
      ...v,
      label: this.language === 'english' ? v.engLabel : v.itaLabel
    }));
  }
}
